import { InfoCircleFilled } from '@ant-design/icons';
import { landmineNames } from '@mm/constants/landmine-names';
import Actions from '@mm/state/actions/actions';
import MessengerAdCRUDActions from '@mm/state/actions/MessengerAdCRUDActions';
import PageCRUDActions from '@mm/state/actions/PageCRUDActions';
import { useWidgetCRUDActions } from '@mm/state/actions/WidgetCRUDActions';
import { MessengerAdController } from '@mm/state/controllers/MessengerAdController';
import { SessionObject } from '@mm/state/interfaces/session';
import AdAccountModel from '@mm/state/model/AdAccountModel';
import { AdSyncModel } from '@mm/state/model/AdModel';
import ConnectedPageModel from '@mm/state/model/ConnectedPageModel';
import {
  ExtendedMessengerAdModel,
  MessengerAdModel,
} from '@mm/state/model/MessengerAdModel';
import { MessengerAdRemoteAdModel } from '@mm/state/model/MessengerAdRemoteAdModel';
import PageModel from '@mm/state/model/PageModel';
import modalActions from '@mm/state/modules/modalsState/actions';
import { selectMessengerAds } from '@mm/state/selectors/adConfigSelectors';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import {
  activeChatbotIdSelector,
  activeChatbotSelector,
} from '@mm/state/selectors/viewSelectors';
import { widgetsByDialogueSelector } from '@mm/state/selectors/widgetSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import classnames from 'classnames';
import {
  chain,
  debounce,
  isArray,
  isEmpty,
  isEqual,
  keys,
  map,
  noop,
  omit,
  omitBy,
  take,
  uniq,
  uniqBy,
} from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Label } from 'reactstrap';
import { Dispatch } from 'redux';
import useRouter from 'use-react-router';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { AccountErrorTooltip } from '../../../../../common/components/AccountErrorTooltip/AccountErrorTooltip';
import CollapsibleContentContainer from '../../../../../common/components/CollapsibleContentContainer/CollapsibleContentContainer';
import { ErrorTooltip } from '../../../../../common/components/ErrorTooltip/ErrorTooltip';
import {
  generateBasicStructure,
  nameById,
} from '../../../../../common/components/FilterableSelect/filterable-select-helpers';
import FilterableSelect from '../../../../../common/components/FilterableSelect/FilterableSelect';
import { addNamesToAccounts } from '../../../../../common/helpers/utils';
import { useBotChannels } from '../../../../../common/hooks/use-bot-channels';
import { useFormSteps } from '../../../../../common/hooks/use-form-steps';
import { usePrevious } from '../../../../../common/hooks/use-previous';
import {
  PathGraphContext,
  useNewGraphInstance,
} from '../../../../../common/widgets/common/PathGraph/PathGraph';
import { DialogueSettingsButton } from '../../../../common/DialogueSettingsButton/DialogueSettingsButton';
import { useInactiveBotActivator } from '../../../../common/hooks/use-inactive-bot-activator';
import { InactiveBotAlert } from '../../../../components/InactiveBotAlert/InactiveBotAlert';
import { DialogueHeaderWarningAlert } from '../../../common/components/DialogueHeaderWarningAlert/DialogueHeaderWarningAlert';
import { EmbeddableDialogueEditorWithPreview } from '../../../startBotPromoters/common/StartBotsDialogueEditor/EmbeddableDialogueEditorWithPreview';
import { StartBotsDialogueEditor } from '../../../startBotPromoters/common/StartBotsDialogueEditor/StartBotsDialogueEditor';
import { StartBotAdsSelector } from '../../../startBotPromoters/common/StartBotSelector/StartBotSelector';
import { MESSAGE_TEXT, SAVE_ADS, SYNC_ADS } from '../../constants/constants';
import { cleanupFetcher, onClickSync, statefulPoller } from '../../utils/utils';
import { formStepContainerBuilder } from '../AdConfigDetail/FormStepContainer/FormStepContainer';
import '../AdDetail.pcss';
import { November2020EuroAdsChangesAlert } from '../November2020EuroAdsChangesAlert/November2020EuroAdsChangesAlert';
import { EditMessengerAdHeader } from './EditMessengerAdHeader/EditMessengerAdHeader';
import { ManualSync } from './ManualSync';
import './MessengerAdDetail.pcss';
import { SelectedAds } from './SelectedAds';
import { SetupDripCampaign } from './SetupDripCampaign';

const SHOW_MORE_LIMIT = 5;

type RouteParams = { chatbotId: string; messengerAdId: string };

const prepareValidations = (remoteValidations: {
  [key in keyof FormValues]?: string[];
}) => {
  return omitBy(
    {
      ...remoteValidations,
    },
    isEmpty,
  );
};

const prepareEmptyFormValues = (accounts: AdAccountModel[]) => {
  const account = accounts[0];
  return {
    name: '',
    adCampaignId: null,
    adAccountId: account?.id || null,
    ads: [],
  };
};

const prepareInitialFormValues = (
  messengerAd: MessengerAdModel | null,
  accounts: AdAccountModel[],
) => {
  if (messengerAd === null) {
    return prepareEmptyFormValues(accounts);
  }
  return {
    name: messengerAd.name,
    adCampaignId: messengerAd.adCampaignId,
    adAccountId: messengerAd.adAccountId,
    ads: messengerAd.ads,
  };
};

type FormValues = ReturnType<typeof prepareInitialFormValues>;

const stepFields = [[], ['adAccountId', 'adCampaignId', 'ads']];

const isValidStep = (
  fields: string[],
  validations: { [key: string]: string[] },
) => {
  return (
    keys(validations).find(
      key => fields.findIndex(field => field === key) !== -1,
    ) === undefined
  );
};

const adsPoller = statefulPoller<AdSyncModel>();

type Props = {
  messengerAdEntity?: MessengerAdModel;
  active?: boolean;
  indexOffset?: number;
  children?: (props: {
    startOver: () => void;
    currentStep: number;
    currentAd: MessengerAdModel | null;
  }) => JSX.Element | JSX.Element[] | null;
  usedInSlideout?: boolean;
  onActivateSuccess?: () => void;
};

// tslint:disable:cyclomatic-complexity
export const MessengerAdDetail = (props: Props) => {
  const {
    messengerAdEntity,
    active = true,
    indexOffset = 0,
    children,
    usedInSlideout,
    onActivateSuccess,
  } = props;
  const {
    match: {
      params: { chatbotId, messengerAdId },
    },
    history,
  } = useRouter<RouteParams>();
  const activeBotId = useSelector(activeChatbotIdSelector);
  const botId = activeBotId || Number(chatbotId);

  const adId = Number(messengerAdId || messengerAdEntity?.id);

  // selectors
  const [adAccounts, facebookPage, session]: [
    AdAccountModel[],
    ConnectedPageModel | undefined,
    SessionObject,
  ] = useSelector((state: StoreState) => {
    return [
      state.adAccounts.entities,
      activeFacebookPageSelector(state),
      state.session.session,
    ];
  }, shallowEqual);

  const activeBot = useSelector(activeChatbotSelector);
  const [page, setPage] = useState<PageModel | null>(null);
  const pageWidgets = useSelector((state: StoreState) =>
    widgetsByDialogueSelector(
      page ? page.id : undefined,
      state.widget.entities,
    ),
  );
  const accountsWithNamesFetcher = useCallback(
    () => uniqBy(addNamesToAccounts(adAccounts), 'id'),
    [adAccounts],
  );
  const accounts = accountsWithNamesFetcher();

  // state
  const [accountId, setAccountId] = useState('');
  const allAds = useSelector((state: StoreState) => state.adEntities.syncedAds);
  const ads: AdSyncModel[] = allAds[accountId]?.data || [];
  const showAdsSpinner = Boolean(allAds[accountId]?.next);
  const [messengerAd, setMessengerAd] = useState<MessengerAdModel | null>(null);
  const [localAds, setLocalAds] = useState<MessengerAdRemoteAdModel[]>([]);
  const synced = !messengerAd?.draft;
  const [failingAds, setFailingAds] = useState<{ [key: string]: string }>({});
  const [remoteValidations, setRemoteValidations] = useState<{
    [key in keyof FormValues]?: string[];
  }>({});
  const [touchedForms, setTouchedForms] = useState(0);
  const [nextClicked, setNextClicked] = useState(-1);
  const [fullyInitialized, setFullyInitialized] = useState(false);
  const [noWidgetChanges, setNoWdigetChanges] = useState(true);
  const [formValues, setFormValues] = useState<FormValues>(
    prepareInitialFormValues(null, accounts),
  );
  const noSelectedAccount =
    accounts.find(account => account.id === messengerAd?.adAccountId) ===
    undefined;
  const connected =
    Boolean(messengerAd?.adAccountId) &&
    (Boolean(messengerAd?.userConnected) || !noSelectedAccount);
  const restrictedAccess =
    Boolean(messengerAd?.adAccountId) && noSelectedAccount;

  const [code, setCode] = useState<string>('');
  const [showMore, setShowMore] = useState(false);
  const { activateBotGuard } = useInactiveBotActivator();
  const { activateChannelGuard } = useBotChannels();

  // effects and calculable values

  const prevPageWidgets = usePrevious({ widgets: pageWidgets });
  const widgetCRUDActions = useWidgetCRUDActions();
  const dispatch: Dispatch<StoreState> = useDispatch();
  const validations = prepareValidations(remoteValidations);
  const valid = isEmpty(validations) && connected;
  const {
    adsUnlimitedNewCtmAds: canHaveUnlimitedMessengerAds,
    adsNewCtmAdsManualSync: canManuallySync,
    adsOneNewCtmAd: pro = false,
  } = useSelector(combinedFeatureFlagsSelector);
  const messengerAds = useSelector(selectMessengerAds).filter(a => !a.draft);
  const armLandmine =
    !pro || (!canHaveUnlimitedMessengerAds && (messengerAds?.length || 0) > 1);
  const campaigns = useMemo(() => {
    const campaignsHash = ads.reduce((acc: { [key: string]: string }, ad) => {
      if (ad.adAccountId !== formValues.adAccountId) {
        return acc;
      }
      acc[ad.campaignId] = ad.campaignName;
      return acc;
    }, {});
    return map(campaignsHash, (value, key) => ({ id: key, name: value }));
  }, [ads, formValues.adAccountId]);
  const campaignAds: AdSyncModel[] = useMemo(() => {
    return ads.filter(ad => ad.campaignId === formValues.adCampaignId);
  }, [formValues.adCampaignId, ads]);

  const adSets: { [key: string]: AdSyncModel[] } = useMemo(() => {
    if (formValues.adCampaignId === null) {
      return {};
    }
    return chain(campaignAds)
      .orderBy(['adSetName'])
      .thru(ads => (showMore ? ads : take(ads, SHOW_MORE_LIMIT)))
      .groupBy(ad => ad.adSetId)
      .value();
  }, [formValues.adCampaignId, showMore, ads]);
  const radioLabelClasses = classnames('ad-config-detail__radio-label', {
    'ad-config-detail__radio-label--disabled': synced,
  });
  const [automaticSyncLocal, setAutomaticSyncLocal] = useState(false);
  const autoSync =
    !messengerAd || messengerAd.adAccountId !== null || automaticSyncLocal;
  const prepareAds = async (accountId: string) => {
    let adsResolved: (data: AdSyncModel[]) => void = noop;
    const adsPromise = new Promise<AdSyncModel[]>(resolve => {
      adsResolved = resolve;
      return;
    });
    const adsFetcher = async (next?: string | null) => {
      const results = await MessengerAdController.availableAds({
        accountId,
        botId,
        limit: 30,
        next: next || allAds[accountId]?.next || undefined,
      });
      adsResolved(results.data);
      return results;
    };
    const onAdsFetched = (data: AdSyncModel[], next: string | null) =>
      dispatch({
        type: Actions.RETRIEVED_SYNCED_ADS,
        payload: { id: accountId, data, next },
      });
    dispatch({ type: Actions.AD_ENTITIES_BUSY });
    if (allAds[accountId]?.data !== undefined) {
      adsResolved(allAds[accountId].data);
    }
    if (allAds[accountId]?.data === undefined || allAds[accountId]?.next) {
      adsPoller(accountId, adsFetcher, onAdsFetched);
    }
    adsPromise
      .then(() => {
        dispatch({ type: Actions.AD_ENTITIES_UNBUSY });
        return null;
      })
      .catch(() => {
        dispatch({ type: Actions.AD_ENTITIES_UNBUSY });
      });
    return adsPromise;
  };

  useEffect(() => {
    (async () => {
      let ad: MessengerAdModel;
      if (messengerAdEntity) {
        ad = messengerAdEntity;
      } else {
        ad = await dispatch(MessengerAdCRUDActions.retrieveById(botId, adId));
      }
      if (!ad.draft) {
        if (!ad.inProgress) {
          ad = await dispatch(
            MessengerAdCRUDActions.prepareReplacementPage(botId, Number(ad.id)),
          );
        }
        setTouchedForms(2);
        setNextClicked(1);
      } else if (ad.progressStep !== null) {
        setTouchedForms(ad.progressStep === 1 ? 2 : ad.progressStep);
        setNextClicked(ad.progressStep);
      } else {
        setNextClicked(0);
      }
      if (ad.adAccountId && ads.length === 0) {
        await prepareAds(ad.adAccountId);
      }
      setMessengerAd(ad);
      setCode(ad.code);
    })();
    return () => {
      adsPoller(null, cleanupFetcher, noop);
    };
  }, []);
  useEffect(() => {
    if (messengerAd) {
      const failures = messengerAd.ads.reduce(
        (
          acc: { [key: string]: string },
          remoteAd: MessengerAdRemoteAdModel,
        ) => {
          if (remoteAd.syncError) {
            acc[remoteAd.remoteId] = remoteAd.syncError;
          }
          return acc;
        },
        {},
      );
      setFailingAds(failures);
    }
  }, [messengerAd]);
  useEffect(() => {
    if (!messengerAd) {
      return;
    }
    (async () => {
      const pageId = messengerAd.replacementPageId || messengerAd.pageId;
      widgetCRUDActions.retrieveAll(pageId);
      const page = await dispatch(PageCRUDActions.retrieveById(pageId));
      setPage(page);
    })();
  }, [messengerAd?.replacementPageId, messengerAd?.pageId]);
  useEffect(() => {
    if (messengerAd && adAccounts.length > 0 && !fullyInitialized) {
      const values = prepareInitialFormValues(messengerAd, accounts);
      setAccountId(values.adAccountId || '');
      setFormValues(values);
      setFullyInitialized(true);
    }
  }, [adAccounts, messengerAd]);
  const debouncedFetch = useCallback(
    debounce(
      async () => {
        const ad = await dispatch(
          MessengerAdCRUDActions.retrieveById(botId, adId),
        );
        setCode(ad.code);
      },
      2000,
      { leading: false, trailing: true },
    ),
    [adId, botId],
  );
  useEffect(() => {
    const prevWidgets = (prevPageWidgets || { widgets: [] }).widgets;
    if (!isEqual(pageWidgets, prevWidgets) && prevWidgets.length > 0) {
      debouncedFetch();
      setNoWdigetChanges(false);
      setTouchedForms(2);
    }
  }, [pageWidgets]);

  // methods

  const testBot = () => {
    if (facebookPage && page) {
      window.open(
        `http://m.me/${facebookPage.remoteId}?ref=${page.searchToken}_${session.searchToken}`,
      );
    }
  };

  const addRemoteValidation = (text: string[], field?: keyof FormValues) => {
    if (!field) {
      return;
    }
    setRemoteValidations({
      ...remoteValidations,
      [field]: uniq([...(remoteValidations[field] || []), ...text]),
    });
  };

  const removeRemoteValidation = (field?: keyof FormValues) => {
    if (!field) {
      return;
    }
    setRemoteValidations(omit(remoteValidations, field));
  };
  const updateRemoteEntity = async (
    updates: Partial<MessengerAdModel>,
    mapValidationField?: keyof FormValues,
  ) => {
    const newEntityOrError = await dispatch(
      MessengerAdCRUDActions.update(botId, updates),
    );
    if (isArray(newEntityOrError)) {
      addRemoteValidation(newEntityOrError, mapValidationField);
    } else {
      removeRemoteValidation(mapValidationField);
      debouncedFetch.cancel();
      setCode(newEntityOrError.code);
      setMessengerAd(newEntityOrError);
    }
  };
  const handleRemoteUpdate = async (
    changes: Partial<{
      [P in keyof ExtendedMessengerAdModel]: ExtendedMessengerAdModel[P];
    }>,
    formChanges?: Partial<{ [P in keyof FormValues]: FormValues[P] }>,
    mapValidationField?: keyof FormValues,
  ) => {
    if (!isEmpty(changes) && messengerAd) {
      const updates = { id: messengerAd.id, ...changes };
      await updateRemoteEntity(updates, mapValidationField);
    }
    if (!isEmpty(formChanges)) {
      setFormValues({ ...formValues, ...formChanges });
    }
  };

  const onSelectAutoSync = async () => {
    let ad = ads[0];
    let account;
    if (ad !== undefined) {
      account = accounts.find(a => a.id === ad.adAccountId);
      if (account === undefined) {
        return;
      }
    }
    if (accounts?.length) {
      account = accounts[0];
      const adsPromise = await prepareAds(account.id);
      const ads = await adsPromise;
      ad = ads[0];
    }

    if (!account) {
      setAutomaticSyncLocal(true);
    }

    handleRemoteUpdate(
      {
        adAccountId: account ? account.id.toString() : null,
        adAccountName: account ? account.name : null,
        adCampaignId: ad?.campaignId || null,
        adCampaignName: ad?.campaignName || null,
        differentUser: account ? account.differentUser : null,
      },
      {
        adAccountId: account ? account.id.toString() : null,
        adCampaignId: ad?.campaignId || null,
      },
    );
  };

  const onSelectManualSync = () => {
    setAutomaticSyncLocal(false);
    if (canManuallySync || !autoSync) {
      handleRemoteUpdate(
        {
          adAccountId: null,
          adAccountName: null,
          adCampaignId: null,
          adCampaignName: null,
          ads: [],
        },
        { adAccountId: null, adCampaignId: null, ads: [] },
      );
    } else {
      dispatch(
        modalActions.openUniversalUpgradeLandmineModal({
          landmineName: landmineNames['MESSAGING-ADS__CLICK_MANUAL_SYNC'],
          feature: 'adsNewCtmAdsManualSync',
        }),
      );
    }
  };

  const handleAccountChange = async (id: string) => {
    if (!messengerAd || !messengerAd.draft) {
      return;
    }
    const account = adAccounts.find((a: AdAccountModel) => a.id === id);
    if (!account) {
      return;
    }
    setAccountId(id);
    const ads = await prepareAds(account.id);
    const campaignId = ads[0]?.campaignId || null;
    handleRemoteUpdate(
      {
        adAccountId: account.id.toString(),
        adAccountName: account.name,
        adCampaignId: campaignId,
        adCampaignName: ads[0]?.campaignName || null,
        differentUser: account.differentUser,
        ads: [],
      },
      {
        adAccountId: account.id.toString(),
        adCampaignId: campaignId === null ? null : campaignId.toString(),
        ads: [],
      },
    );
  };

  const handleCampaignChange = (id: string) => {
    if (!messengerAd || !messengerAd.draft) {
      return;
    }
    const campaign = campaigns.find(c => c.id === id);
    campaign &&
      handleRemoteUpdate(
        {
          adCampaignId: campaign.id.toString(),
          adCampaignName: campaign.name,
          ads: [],
        },
        { adCampaignId: campaign.id.toString(), ads: [] },
      );
  };

  const handleNameSave = (name: string) => {
    handleRemoteUpdate({ name }, {}, 'name');
  };

  const onAdClicked = (id: string, checked: boolean) => {
    let changes;
    let formChanges;
    let newLocalAds;
    if (checked) {
      const ad = ads.find(ad => ad.remoteId === id);
      if (ad === undefined) {
        return;
      }
      const newAd: MessengerAdRemoteAdModel = {
        remoteId: ad.remoteId,
        name: ad.name,
        syncError: failingAds[ad.remoteId] || null,
        synced: false,
      };
      changes = { ads: [...(messengerAd?.ads || []), newAd] };
      formChanges = { ads: [...formValues.ads, newAd] };
      newLocalAds = [...localAds, newAd];
    } else {
      const condition = (ad: MessengerAdRemoteAdModel) => ad.remoteId !== id;
      formChanges = { ads: formValues.ads.filter(condition) };
      changes = { ads: (messengerAd?.ads || []).filter(condition) };
      newLocalAds = localAds.filter(condition);
    }
    setLocalAds(newLocalAds);
    handleRemoteUpdate(synced ? {} : changes, formChanges);
  };

  const isTouchedStep = useCallback(
    (index: number) => {
      return touchedForms >= index + 1;
    },
    [touchedForms],
  );
  const isValid = useCallback(
    (index: number) => {
      if (index === 0) {
        return true;
      }
      return isValidStep(stepFields[index], validations);
    },
    [validations],
  );

  const setTouchedFormsLevel = (level: number) => {
    setTouchedForms(Math.max(level, touchedForms));
  };

  const wrappedUpdate: <T extends (...args: any) => void>(
    level: number,
    callback: T,
  ) => (...params: Parameters<T>) => void =
    (level, callback) =>
    (...args) => {
      setTouchedFormsLevel(level);
      // @ts-ignore
      callback(...args);
    };

  const importStartBot = useCallback(
    wrappedUpdate(1, async (pageId: number) => {
      if (pageId === page?.id || !messengerAd) {
        return;
      }
      if (messengerAd.draft) {
        return handleRemoteUpdate({ pageId });
      }
      const ad = await dispatch(
        MessengerAdCRUDActions.prepareReplacementPage(botId, adId, pageId),
      );
      setMessengerAd(ad);
    }),
    [messengerAd, page],
  );

  const onSuccess = () =>
    onClickSync(
      dispatch,
      history,
      messengerAd,
      synced ? { ads: localAds } : {},
      botId,
      armLandmine,
      pro,
      ad => setMessengerAd(ad),
      usedInSlideout,
      onActivateSuccess,
    );

  const onSync = () => {
    activateBotGuard()
      .then(() => activateChannelGuard(['facebook']))
      .then(onSuccess)
      .catch(() => {}); // handled in the guard
  };

  const renderButton = () => (
    <PrimaryButton
      className={classnames('ad-detail__activate-button', {
        'ad-detail__activate-button-slideout': usedInSlideout,
      })}
      onClick={onSync}
      disabled={
        !messengerAd ||
        !valid ||
        (messengerAd.draft
          ? messengerAd.ads.length === 0
          : noWidgetChanges && localAds.length === 0) ||
        activeBot.template
      }
      color={ButtonColor.blue}
    >
      {!messengerAd || messengerAd.draft ? SYNC_ADS : SAVE_ADS}
    </PrimaryButton>
  );

  const pathGraph = useNewGraphInstance(
    messengerAd?.replacementPageId || messengerAd?.pageId,
  );
  const isCollapsed = (index: number) => {
    if (!active || nextClicked < 0) {
      return true;
    }
    if (index === 0) {
      return false;
    }
    return nextClicked < index;
  };
  const configs = [
    {
      header: !usedInSlideout ? (
        <div className="ad-detail__create-dialogue-header">
          <span>
            Create Dialogue{' '}
            <a
              className="ad-detail__test-bot"
              href="#"
              onClick={e => {
                e.stopPropagation();
                testBot();
              }}
            >
              Test my bot
            </a>
          </span>
          <DialogueHeaderWarningAlert alertClassName="ad-detail--warning-alert" />
        </div>
      ) : (
        'Create Dialogue'
      ),
      renderDropdownOptions: messengerAd
        ? () => {
            return (
              <>
                <DialogueSettingsButton
                  pageId={messengerAd.replacementPageId || messengerAd.pageId}
                />
                <StartBotAdsSelector onBotSelect={importStartBot} />
              </>
            );
          }
        : undefined,
      children: (
        <>
          <div className="ad-detail__note">
            <InfoCircleFilled className="ad-detail__info-circle" />
            <div className="ad-detail__subtext">
              <div>{MESSAGE_TEXT}</div>
            </div>
          </div>
          {messengerAd && !usedInSlideout ? (
            <PathGraphContext.Provider value={pathGraph}>
              <StartBotsDialogueEditor
                chatbotId={botId}
                forAds={true}
                isDisabled={messengerAd.inProgress}
                widgets={pageWidgets}
                hideTopWidgetAdder
                disableFirstWidgetDeleting
                dialogueId={messengerAd.replacementPageId || messengerAd.pageId}
                restrictToChannels={['facebook']}
                platforms={['facebook']}
                showOnlyNameAttributes
              />
            </PathGraphContext.Provider>
          ) : (
            <EmbeddableDialogueEditorWithPreview
              editPaneClassname="ad-detail__embeddable-dialogue-editor"
              dialogueEditorClassName="ad-detail__embeddable-dialogue-editor-preview"
              dialogueId={
                messengerAdEntity?.replacementPageId ||
                messengerAdEntity?.pageId
              }
              isDisabled={messengerAdEntity?.inProgress}
              usedInSlideout={usedInSlideout}
              platforms={['facebook']}
              hideDialogueSelector
              forAds
            />
          )}
        </>
      ),
    },
    {
      header: 'Synced Ads',
      children: (
        <>
          <div
            className={classnames(
              'messenger-ad-detail__sync-selection ad-detail__radio-row',
              { 'ad-detail__radio-column': usedInSlideout },
            )}
          >
            <div className="messenger-ad-detail__auto-sync ad-detail__radio-container">
              <input
                type="radio"
                id="auto-sync-checker"
                className="ad-detail__radio-input"
                disabled={synced}
                checked={autoSync}
                onChange={wrappedUpdate(2, onSelectAutoSync)}
              />
              <Label className={radioLabelClasses} for="auto-sync-checker">
                Sync automatically (<b>Recommended</b>)
              </Label>
            </div>
            <div className="messenger-ad-detail__manual-sync ad-detail__radio-container">
              <input
                type="radio"
                id="manual-sync-checker"
                className="ad-detail__radio-input"
                disabled={synced}
                checked={!autoSync}
                onChange={wrappedUpdate(2, onSelectManualSync)}
              />
              <Label className={radioLabelClasses} for="manual-sync-checker">
                Connect manually
              </Label>
            </div>
          </div>
          {autoSync && (
            <>
              <div className="ad-detail__inputs-container">
                <div
                  className={classnames('ad-detail__input-container', {
                    'ad-detail__input-container-slideout': usedInSlideout,
                  })}
                >
                  <div className="ad-detail__field-title">Ad Account</div>
                  <div className="messenger-ad-detail__ad-account-selector ad-detail__selector">
                    <FilterableSelect
                      onChange={wrappedUpdate(2, handleAccountChange)}
                      outerValue={nameById(
                        formValues.adAccountId || '',
                        accounts.map(account => ({
                          ...account,
                          id: Number(account.id),
                        })),
                        restrictedAccess
                          ? `${messengerAd?.adAccountName} (Access Restricted)`
                          : 'No Ad Account',
                      )}
                      suggestions={generateBasicStructure(accounts)}
                      placeholder="Select Ad Account"
                      disabled={synced}
                    />
                    <AccountErrorTooltip
                      classPrefix="ad-detail"
                      hasAccounts={accounts.length > 0}
                      validation={validations['adAccount']}
                    />
                  </div>
                </div>
                <div
                  className={classnames('ad-detail__input-container', {
                    'ad-detail__input-container-slideout': usedInSlideout,
                  })}
                >
                  <div className="ad-detail__field-title">Campaign</div>
                  <div className="messenger-ad-detail__ad-campaign-selector ad-detail__selector">
                    <FilterableSelect
                      onChange={wrappedUpdate(2, handleCampaignChange)}
                      showSpinner={showAdsSpinner}
                      dynamicChangeSuggestions={true}
                      outerValue={nameById(
                        formValues.adCampaignId || '',
                        campaigns.map(campaign => ({
                          ...campaign,
                          id: Number(campaign.id),
                        })),
                        restrictedAccess && messengerAd?.adCampaignId
                          ? `${messengerAd.adCampaignName} (Access Restricted)`
                          : 'No Matching Campaign',
                      )}
                      suggestions={generateBasicStructure(campaigns)}
                      placeholder="Select Campaign"
                      disabled={synced}
                    />
                    <ErrorTooltip
                      classPrefix="ad-detail"
                      validation={validations['adAccount']}
                      hoverMode
                    />
                  </div>
                </div>
              </div>
              <SelectedAds
                adSets={adSets}
                id={adId}
                selectedAds={formValues.ads}
                restricted={restrictedAccess}
                failingAds={failingAds}
                needsShowMore={campaignAds.length > SHOW_MORE_LIMIT}
                showMoreExtended={showMore}
                onClickShowMore={() => setShowMore(!showMore)}
                onClickAd={wrappedUpdate(2, onAdClicked)}
              />
              {renderButton()}
            </>
          )}
          {!autoSync && (
            <ManualSync
              code={code}
              refString={messengerAd?.searchToken || ''}
              usedInSlideout={usedInSlideout}
            />
          )}
        </>
      ),
    },
  ];

  const [stepsStatus, setStepsStatus] = useFormSteps(
    configs,
    isCollapsed,
    nextClicked,
  );
  const onNext = (index: number, allowed?: boolean) => {
    setTouchedForms(Math.max(touchedForms, index + 1));
    if (allowed) {
      setNextClicked(index + 1);
      if (
        messengerAd?.draft &&
        (messengerAd.progressStep === null ||
          messengerAd.progressStep < index + 1)
      ) {
        setMessengerAd({ ...messengerAd, progressStep: index + 1 });
        dispatch(
          MessengerAdCRUDActions.update(botId, {
            id: messengerAd.id,
            progressStep: index + 1,
          }),
        );
      }
    }
  };
  const formSteps = formStepContainerBuilder({
    configs,
    options: {
      className: 'ad-detail__form-step',
      contentClassName: 'ad-detail__form-step-collapsible-content',
      indexOffset,
      isCollapsed,
      isTouched: isTouchedStep,
      isValid,
      nextAllowed: (index: number) => isValid(index) || nextClicked > index,
      onNext,
      stepsStatus,
      setStepsStatus,
    },
  });

  // render

  return (
    <div
      className={
        !usedInSlideout ? 'ad-detail-container' : 'ad-detail-container-slideout'
      }
    >
      <EditMessengerAdHeader
        messengerAd={messengerAd}
        showPublish={autoSync}
        onSaveClick={onSync}
        canPublish={
          valid &&
          !messengerAd?.inProgress &&
          autoSync &&
          (messengerAd?.draft
            ? messengerAd.ads.length > 0
            : !noWidgetChanges || localAds.some(Boolean))
        }
        onSaveName={handleNameSave}
        errors={validations['name']}
        isTemplateBot={!!activeBot.template}
        showOnlyActionButton={usedInSlideout}
        actionButtonClassName={
          usedInSlideout ? 'ad-detail__slideout-header-action-button' : ''
        }
      />
      <InactiveBotAlert />
      <November2020EuroAdsChangesAlert />
      {children?.({
        startOver: () => {
          onNext(-1, true);
          setStepsStatus([true, ...stepsStatus.slice(1).map(() => false)]);
        },
        currentAd: messengerAd,
        currentStep: nextClicked,
      })}
      {!usedInSlideout
        ? formSteps
        : configs.map((item, index) => {
            return (
              <CollapsibleContentContainer
                key={item.header.toString()}
                orderNumber={(index + 2).toString()}
                containerName={item.header}
                className="ad-detail__slideout-step-container"
                stepperStylingPlatform="facebook"
                useCircleNumberStyle
              >
                {item.children}
              </CollapsibleContentContainer>
            );
          })}
      {messengerAdEntity && usedInSlideout && (
        <SetupDripCampaign
          messengerAdId={messengerAdEntity.id}
          collapsed={false}
          usedInSlideout={usedInSlideout}
          orderNumber={(configs.length + 2).toString()}
        />
      )}
      {!usedInSlideout && (
        <SetupDripCampaign messengerAdId={adId} collapsed={nextClicked < 1} />
      )}
    </div>
  );
};
